import { Box, Stack, styled, Text } from '@ltvco/refresh-lib/theme';

import { hideScrollbar } from 'components/DeepSearch/styleMixins';

interface DeepSearchLandingPageContainerProps {
  isTransitioning: boolean;
}

interface SlideOutProps {
  isAnimating: boolean;
  slideY: number;
}

const slideOutStyle = (isAnimating: boolean, slideY: number) => ({
  transform: isAnimating ? `translateY(${slideY}px)` : 'translateY(0px)',
  opacity: isAnimating ? 0 : 1,
  transition:
    'transform 1s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.65s cubic-bezier(0.4, 0, 0.6, 1) 0.2s',
  willChange: 'transform, opacity' as const,
});

const animationPropForwardFilter = (prop: PropertyKey) =>
  prop !== 'isAnimating' && prop !== 'slideY' && prop !== 'chatOpacity';

export const PageContainer = styled(Box)(({ theme }) => ({
  height: '98%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '16px',
  margin: '0px',
  overflow: 'auto',
  position: 'relative',
  paddingBottom: 'env(safe-area-inset-bottom)',
  fontFamily: "'Nunito Sans', sans-serif",
  [theme.breakpoints.up('md')]: {
    // Match the larger-screen (lg) top spacing — the 30px here made the chat
    // container sit lower below 1200 than it does on bigger screens.
    margin: '8px 0',
    paddingBottom: 0,
  },
  [theme.breakpoints.up('lg')]: {
    margin: '8px',
  },
}));

export const DeepSearchLandingPageContainer = styled(Box, {
  // Keep the styling-only boolean off the DOM (React warns about unknown
  // attributes otherwise).
  shouldForwardProp: (prop) => prop !== 'isTransitioning',
})<DeepSearchLandingPageContainerProps>(({ theme, isTransitioning }) => ({
  padding: '30px 15px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '16px',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflowX: 'hidden',
  // Scroll on short viewports (chips/terms stay reachable); hidden mid-animation
  // so the off-screen slide doesn't flash a scrollbar.
  overflowY: isTransitioning ? 'hidden' : 'auto',
  zIndex: isTransitioning ? 1 : 2,
  [theme.breakpoints.up('md')]: {
    padding: '30px',
  },
}));

export const MessagesArea = styled(Box)({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  ...hideScrollbar,
  padding: '20px 16px 24px',
  '@media (min-width: 1025px)': {
    paddingTop: '40px',
  },
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  maxWidth: '800px',
  width: '100%',
  margin: '0 auto',
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  animation: 'fadeIn 0.5s ease-in-out',
});

export const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser?: boolean }>(({ theme, isUser }) => ({
  maxWidth: isUser ? '80%' : '100%',
  padding: '12px 16px',
  borderRadius: '12px',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  backgroundColor: isUser ? theme.palette.background.light : 'transparent',
  color: theme.palette.text.primary,
  boxShadow: 'none',
  wordBreak: 'break-word',
  lineHeight: 1.5,
  fontSize: '1rem',
  whiteSpace: 'pre-wrap',
  // Extra space above each user question so Q&A turns read as groups (~40px).
  ...(isUser && { marginTop: '24px' }),
}));

export const MarkdownContent = styled(Box)(({ theme }) => ({
  whiteSpace: 'normal',
  '& > :first-of-type': { marginTop: 0 },
  '& > :last-child': { marginBottom: 0 },
  '& p': { margin: '0 0 0.75em' },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: '1em 0 0.5em',
    lineHeight: 1.3,
    fontWeight: 600,
  },
  '& h1': { fontSize: '1.5rem' },
  '& h2': { fontSize: '1.3rem' },
  '& h3': { fontSize: '1.15rem' },
  '& ul, & ol': { margin: '0 0 0.75em', paddingLeft: '1.5em' },
  '& li': { marginBottom: '0.25em' },
  '& li > p': { margin: 0 },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
  '& code': {
    fontFamily: 'monospace',
    fontSize: '0.875em',
    padding: '0.1em 0.35em',
    borderRadius: '4px',
    backgroundColor: theme.palette.background.light,
  },
  '& pre': {
    margin: '0 0 0.75em',
    padding: '12px',
    borderRadius: '8px',
    overflowX: 'auto',
    backgroundColor: theme.palette.background.light,
  },
  '& pre code': { padding: 0, backgroundColor: 'transparent' },
  '& blockquote': {
    margin: '0 0 0.75em',
    paddingLeft: '1em',
    borderLeft: `3px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  },
  '& table': { borderCollapse: 'collapse', margin: '0 0 0.75em' },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: '6px 10px',
  },
}));

export const LandingContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  gap: '24px',
});

export const LandingTitle = styled('h1')(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  margin: 0,
  textAlign: 'center',
}));

export const LandingSubtitle = styled('p')(({ theme }) => ({
  fontSize: '16px',
  color: theme.palette.text.secondary,
  margin: 0,
  textAlign: 'center',
  maxWidth: '480px',
}));

export const ThinkingStepsIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '15px',
  alignSelf: 'flex-start',
  alignItems: 'center',
  padding: '4px 0',
  fontSize: '14px',
  color: theme.palette.text.secondary,
}));

export const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
});

// Wraps the landing input. Slides + fades down off-screen when isAnimating.
export const SlideOutInputWrapper = styled(Box, {
  shouldForwardProp: animationPropForwardFilter,
})<SlideOutProps>(({ isAnimating, slideY }) => ({
  width: '100%',
  // slideOutStyle's transform makes this wrapper (and the chips wrapper) each their
  // own stacking context. Without an explicit z-index, the later-painted chips sit on
  // top, hiding the suggestions dropdown that opens downward from the input. Lift the
  // input subtree above the chips so the dropdown floats over them.
  position: 'relative',
  zIndex: 2,
  ...slideOutStyle(isAnimating, slideY),
}));

// Wraps the chips. Same slide+fade, plus its own responsive width.
export const SlideOutChipsStack = styled(Stack, {
  shouldForwardProp: animationPropForwardFilter,
})<SlideOutProps>(({ theme, isAnimating, slideY }) => ({
  width: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  [theme.breakpoints.up('md')]: { width: '100%' },
  [theme.breakpoints.down('md')]: { width: '90%' },
  ...slideOutStyle(isAnimating, slideY),
}));

// Wraps the terms/disclaimer. Same slide+fade + zeroes the inner margin.
export const SlideOutTermsStack = styled(Stack, {
  shouldForwardProp: animationPropForwardFilter,
})<SlideOutProps>(({ isAnimating, slideY }) => ({
  width: '100%',
  '& [data-terms-element]': { marginTop: '0 !important' },
  ...slideOutStyle(isAnimating, slideY),
}));

interface ChatViewInputWrapperProps {
  chatOpacity: number;
}

// Chat-view input entrance: soft opacity reveal once mounted — no translate,
// so the input doesn't compete with the landing's slide-off motion. A long
// ease-in-out gives a gentle, gradual appearance.
export const ChatViewInputWrapper = styled(Box, {
  shouldForwardProp: animationPropForwardFilter,
})<ChatViewInputWrapperProps>(({ chatOpacity }) => ({
  opacity: chatOpacity,
  transition: 'opacity 0.9s cubic-bezier(0.4, 0, 0.2, 1) 0.1s',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  willChange: 'opacity',
}));

interface FadeOutBoxProps {
  isAnimating: boolean;
}

// Simple opacity fade — used for the landing title and description.
export const FadeOutBox = styled(Box, {
  shouldForwardProp: animationPropForwardFilter,
})<FadeOutBoxProps>(({ isAnimating }) => ({
  transition: 'opacity 0.5s ease-in-out',
  opacity: isAnimating ? 0 : 1,
}));

type LogoAnimationPhase = 'center' | 'moving-up' | 'moving-left' | 'header';

// Builds the sx for BrandLogoChat's phase-based animation. Kept as an sx
// builder (not styled()) because BrandLogoChat's own internal styles take
// precedence over a styled() wrapper for transform — passing via sx merges
// at the level where the styles actually apply.
export const buildLogoAnimationSx = (
  isAnimating: boolean,
  phase: LogoAnimationPhase,
  translationX: number,
  translationY: number
) => ({
  margin: '20px 0',
  transition:
    isAnimating && phase === 'header'
      ? 'opacity 0.45s cubic-bezier(0.4, 0, 0.2, 1)'
      : 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
  ...(isAnimating &&
    phase === 'moving-up' && {
      transform: `translateY(${translationY}px) scale(0.88)`,
    }),
  ...(isAnimating &&
    phase === 'moving-left' && {
      transform: `translateY(${translationY}px) translateX(${translationX}px) scale(0.78)`,
    }),
  ...(isAnimating &&
    phase === 'header' && {
      transform: `translateY(${translationY}px) translateX(${translationX}px) scale(0.78)`,
      opacity: 0,
    }),
});

// Landing page layout: outermost flex column with top section + bottom terms.
export const LandingContentOuterStack = styled(Stack)({
  flexDirection: 'column',
  justifyContent: 'space-between',
  width: '100%',
  flex: 1,
});

// Logo, title and content are centered from `sm` up and left-aligned below it
// (phones), matching the title's text-align — so they never diverge between
// tablet and phone. (sm = useScreenSize's `isMobile` breakpoint.)
export const LandingContentInnerStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: '10%',
  width: '100%',
  paddingLeft: '15px',
  paddingRight: '15px',
  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start',
  },
  [theme.breakpoints.up('md')]: {
    paddingLeft: '30px',
    paddingRight: '30px',
  },
}));

export const LogoTextGroupStack = styled(Stack)(({ theme }) => ({
  width: '100%',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start',
  },
}));

// Landing title: a block of inline phrases. Each phrase is nowrap (so "Ask
// More." / "Discover More" never break mid-phrase) and they wrap as whole
// units. text-align (set by the caller) centers the wrapped lines reliably —
// flex justify-content can't center wrapped content predictably.
export const TitleRow = styled(Text)(({ theme }) => ({
  display: 'block',
  fontSize: '2.625rem',
  fontWeight: 'bold',
  lineHeight: 1.15,
  margin: 0,
  marginBottom: theme.spacing(2.25),
}));

export const TitlePhrase = styled('span')({
  whiteSpace: 'nowrap',
});

export const TitleHighlight = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
}));

// "Thinking..." text with a subtle moving-highlight shimmer. The base color
// stays at theme.palette.text.secondary; a lighter band sweeps across the
// text via a moving linear-gradient clipped to the text glyphs.
export const ShimmerText = styled(Text)(({ theme }) => ({
  background: `linear-gradient(90deg, ${theme.palette.text.secondary} 0%, ${theme.palette.text.secondary} 40%, #B8BCC4 50%, ${theme.palette.text.secondary} 60%, ${theme.palette.text.secondary} 100%)`,
  backgroundSize: '200% 100%',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
  '@keyframes shimmerSweep': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-200% 0' },
  },
  animation: 'shimmerSweep 2.4s linear infinite',
}));
